<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getConversationScope } from 'dashboard/helper/permissionsHelper';
import { CONVERSATION_PERMISSIONS } from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';

const props = defineProps({
  roles: {
    type: Array,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);

const VISIBLE_TAG_COUNT = 3;

const { t } = useI18n();

const rows = computed(() =>
  props.roles.map(role => {
    // The conversation permissions are summed up by the scope tag, so only the rest gets a tag.
    const tags = role.permissions
      .filter(permission => !CONVERSATION_PERMISSIONS.includes(permission))
      .map(permission =>
        t(`CUSTOM_ROLE.PERMISSION_TAGS.${permission.toUpperCase()}`)
      );

    return {
      role,
      scopeTag: t(
        `CUSTOM_ROLE.CONVERSATION_SCOPE_TAGS.${getConversationScope(
          role.permissions
        ).toUpperCase()}`
      ),
      visibleTags: tags.slice(0, VISIBLE_TAG_COUNT),
      hiddenTags: tags.slice(VISIBLE_TAG_COUNT),
    };
  })
);
</script>

<template>
  <BaseTableRow v-for="row in rows" :key="row.role.id" :item="row.role">
    <template #default>
      <BaseTableCell class="w-[18%]">
        <span class="text-body-main text-n-slate-12 truncate block">
          {{ row.role.name }}
        </span>
      </BaseTableCell>

      <BaseTableCell class="w-[32%]">
        <span class="text-body-main text-n-slate-11 line-clamp-2">
          {{ row.role.description }}
        </span>
      </BaseTableCell>

      <BaseTableCell class="w-[42%]">
        <div class="flex flex-wrap items-center gap-1.5">
          <Label compact color="blue" :label="row.scopeTag" />
          <Label
            v-for="tag in row.visibleTags"
            :key="tag"
            compact
            :label="tag"
          />
          <span
            v-if="row.hiddenTags.length"
            v-tooltip.top="row.hiddenTags.join(', ')"
            class="text-label-small text-n-slate-11"
          >
            +{{ row.hiddenTags.length }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell align="end" class="w-24">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <Button
            v-tooltip.top="$t('CUSTOM_ROLE.EDIT.BUTTON_TEXT')"
            icon="i-woot-edit-pen"
            slate
            sm
            @click="emit('edit', row.role)"
          />
          <Button
            v-tooltip.top="$t('CUSTOM_ROLE.DELETE.BUTTON_TEXT')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            :is-loading="loading[row.role.id]"
            @click="emit('delete', row.role)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
